import type {
  FirmBulkAssigneeRequest,
  FirmBulkAssigneeResult,
  FirmCreateRequest,
  FirmListQuery,
  FirmPatchRequest,
} from '@shared/types/firm';
import { api } from './client';

/// firm API クライアント = fetch のみ。レスポンスは「外から来た謎の箱」= `unknown` を返す。
/// 検証・branded 化は domain の `parseFirm` / `parsePaged` 門番が行う (parse, don't validate / #471)。

function buildQueryString(query: FirmListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      // 配列は CSV で送る (backend は ListFirmsQueryDto::split_csv で復元)。
      params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length === 0 ? '' : `?${qs}`;
}

type CsvFirmListQueryKey =
  | 'cities'
  | 'lawyer_count_buckets'
  | 'bar_associations'
  | 'prefectures'
  | 'organization_types'
  | 'practice_areas'
  | 'client_ratio_categories'
  | 'it_literacies'
  | 'competitor_service_ids'
  | 'product_ids'
  | 'assignee_account_ids'
  | 'top_sales_grades'
  | 'next_action_types';

type FirmListQueryRequestBody = Omit<FirmListQuery, CsvFirmListQueryKey> &
  Partial<Record<CsvFirmListQueryKey, string>>;

type FirmBulkAssigneeRequestBody = Omit<FirmBulkAssigneeRequest, 'selection'> & {
  selection:
    | { mode: 'ids'; ids: string[] }
    | { mode: 'query'; query: FirmListQueryRequestBody };
};

function csv(value: readonly string[] | undefined): string | undefined {
  if (!value || value.length === 0) return undefined;
  return value.join(',');
}

function buildQueryRequestBody(query: FirmListQuery): FirmListQueryRequestBody {
  return {
    ...query,
    cities: csv(query.cities),
    lawyer_count_buckets: csv(query.lawyer_count_buckets),
    bar_associations: csv(query.bar_associations),
    prefectures: csv(query.prefectures),
    organization_types: csv(query.organization_types),
    practice_areas: csv(query.practice_areas),
    client_ratio_categories: csv(query.client_ratio_categories),
    it_literacies: csv(query.it_literacies),
    competitor_service_ids: csv(query.competitor_service_ids),
    product_ids: csv(query.product_ids),
    assignee_account_ids: csv(query.assignee_account_ids),
    top_sales_grades: csv(query.top_sales_grades),
    next_action_types: csv(query.next_action_types),
  };
}

function buildBulkAssigneeRequestBody(
  body: FirmBulkAssigneeRequest,
): FirmBulkAssigneeRequestBody {
  if (body.selection.mode === 'ids') {
    return {
      target_assignee_account_id: body.target_assignee_account_id,
      only_unassigned: body.only_unassigned,
      selection: body.selection,
    };
  }
  return {
    ...body,
    selection: {
      mode: 'query',
      query: buildQueryRequestBody(body.selection.query),
    },
  };
}

export function listFirms(query: FirmListQuery = {}): Promise<unknown> {
  return api.get(`/api/firms${buildQueryString(query)}`);
}

export function getFirm(id: string): Promise<unknown> {
  return api.get(`/api/firms/${encodeURIComponent(id)}`);
}

export function createFirm(body: FirmCreateRequest): Promise<unknown> {
  return api.post('/api/firms', body);
}

export function patchFirm(id: string, body: FirmPatchRequest): Promise<unknown> {
  return api.patch(`/api/firms/${encodeURIComponent(id)}`, body);
}

export function deleteFirm(id: string): Promise<void> {
  return api.delete(`/api/firms/${encodeURIComponent(id)}`);
}

/// Backend #547 の一括担当 API。query mode の対象解釈は backend の FirmListQuery に委ねる。
export function previewFirmBulkAssignee(
  body: FirmBulkAssigneeRequest,
): Promise<FirmBulkAssigneeResult> {
  return api.post<FirmBulkAssigneeResult>(
    '/api/firms/bulk-assignee/preview',
    buildBulkAssigneeRequestBody(body),
  );
}

export function applyFirmBulkAssignee(
  body: FirmBulkAssigneeRequest,
): Promise<FirmBulkAssigneeResult> {
  return api.post<FirmBulkAssigneeResult>(
    '/api/firms/bulk-assignee',
    buildBulkAssigneeRequestBody(body),
  );
}
